/**
 * Reading errors that came back from the API.
 *
 * `ApiError` lives HERE rather than beside the fetch that throws it, so that
 * importing it does not drag in the Supabase client -- which throws at module
 * load when the build-time env vars are missing, and made this file
 * untestable in CI.
 */

/** Thrown for any non-2xx response, carrying the status and parsed body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`API request failed with status ${status}`)
    this.name = 'ApiError'
  }
}

/**
 * The generated hooks type their error as `unknown`, but the RUNTIME value is
 * always an ApiError, because the mutator throws one. Never trust the generated
 * error type; narrow with instanceof.
 */
export const isConflict = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.status === 409

/**
 * FastAPI's error body is `{ detail: ... }`, and the payload is on
 * `error.body` — NOT on the error itself. Getting that wrong is silent: the
 * fallback renders and the real reason never reaches the screen. It cost one
 * bug already, which is why reading it lives here and nowhere else.
 */
function detailOf(error: unknown): unknown {
  if (error instanceof ApiError && typeof error.body === 'object' && error.body !== null) {
    return (error.body as { detail?: unknown }).detail
  }
  return undefined
}

/** One line for the user. */
export function apiMessage(error: unknown, fallback: string): string {
  const detail = detailOf(error)
  if (typeof detail === 'string') return detail
  // Some endpoints answer with a list of reasons; show the first rather than
  // falling back to something vaguer than what we were told.
  if (Array.isArray(detail) && typeof detail[0] === 'string') return detail[0]
  return fallback
}

/**
 * EVERY reason, for endpoints that answer with a list of them.
 *
 * The settings screen validates the whole configuration at once and refuses
 * with all its objections, because a form that reveals them one at a time is
 * how somebody ends up saving four times. This is what puts them on screen.
 */
export function apiProblems(error: unknown, fallback: string): string[] {
  if (!error) return []
  const detail = detailOf(error)
  if (Array.isArray(detail) && detail.every((item) => typeof item === 'string')) {
    return detail as string[]
  }
  if (typeof detail === 'string') return [detail]
  return [fallback]
}
